/** @jsxRuntime classic */
/** @jsx jsx */

import { css, jsx } from '@emotion/react';
import { labelStyles } from '@zapier/style-encapsulation';
import { Colors } from '@zapier/design-system';
import { MouseEventHandler, ReactNode } from 'react';
import { MEDIA_MIN_WIDTH } from '../../constants';
import { getLinkProps } from '../../utils/link';

export type ArrowLinkProps = {
  children: ReactNode;
  href?: string;
  onClick?: MouseEventHandler;
};

const Styles = labelStyles('ArrowLink', {
  root: css({
    position: 'relative',
    width: 'fit-content',
    display: 'inline-flex',
    alignItems: 'center',
    color: Colors.neutral800,
    fontSize: '16px',
    fontWeight: 600,
    textDecoration: 'none',
    lineHeight: '18.5px',
    '&:focus:not([disabled])': {
      outline: 'none',

      '&::before': {
        content: '""',
        position: 'absolute',
        pointerEvents: 'none',
        top: '-8px',
        right: '-16px',
        bottom: '-8px',
        left: '-20px',
        border: `3px solid ${Colors.neutral800}`,
        borderRadius: '31px',
      },
    },

    [MEDIA_MIN_WIDTH.medium]: {
      fontSize: '18px',
    },
  }),

  text: css({
    borderBottom: `1.5px solid ${Colors.neutral800}`,
    padding: `1px 0`,
    transition: 'border-color 250ms',

    'a:hover &': {
      borderColor: 'transparent',
    },
  }),

  icon: css({
    width: '1em',
    height: '1em',
    marginLeft: '5px',
    transition: 'margin-left 250ms',

    'a:hover &': {
      marginLeft: '10px',
    },
  }),
});

export function ArrowLink(props: ArrowLinkProps) {
  const { rel, target } = getLinkProps(props.href);

  return (
    <a
      css={Styles.root}
      href={props.href}
      onClick={props.onClick}
      target={target}
      rel={rel}
    >
      <span css={Styles.text}>{props.children}</span>
      <svg
        css={Styles.icon}
        viewBox="0 0 24 24"
        fill="currentColor"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path d="M16.86 11H4v2h12.86l-5.04 6h2.62l5.86-7-5.86-7h-2.62l5.04 6z" />
      </svg>
    </a>
  );
}
